import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  useMemo,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import {
  STORYBOARDS_FOLDER,
  getUnexpiredToken,
  shuffleArray,
} from "@/utils/utils";
import {
  dequeueElement,
  enqueueElements,
  initializeQueue,
  markElementsWithForce,
  setIsFetching,
  setRegenerateEntireQueue,
} from "@/store/metadataSlice";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { useCanvasId } from "@/utils/utils";
import { useSupabase } from "./DatabaseSyncWrapper";
import { useSession } from "@clerk/clerk-react";
const ElementMetadataContext = createContext({});

interface Props {
  children: React.ReactNode;
  framework?: string;
}

export const ElementMetadataProvider = React.memo(
  ({ children, framework }: Props) => {
    const [metadata, setMetadata] = useState({});
    const dispatch = useDispatch();

    const canvasId = useCanvasId();

    const treeElementLookup = useCodebaseStore(
      (state) => state.treeElementLookup
    );
    const projectLaunchStatus = useCodebaseStore(
      (state) => state.projectLaunchStatus
    );

    const isFetching = useSelector((state: any) => state.metadata.isFetching);
    const elementsToFetch = useSelector(
      (state: any) => state.metadata.elementsToFetch
    );
    const regenerateEntireQueue = useSelector(
      (state: any) => state.metadata.regenerateEntireQueue
    );

    const { triggerRefetchElementMetadata } = useSupabase();

    const { session } = useSession();
    const processingInformation = useSelector(
      (state: any) => state.metadata.processingInformation
    );

    const projectElementMetadata = useSelector(({ supabaseStore }) => {
      return supabaseStore.elementMetadata;
    });
    const projectElementMetadataLoaded =
      useSelector(({ supabaseStore }) => {
        return supabaseStore.elementMetadataLoadStatus === "succeeded";
      }) && canvasId;

    const hashToElementMetadata = useMemo(() => {
      const newMap = {};
      if (projectElementMetadata) {
        Object.values(projectElementMetadata)?.forEach(
          (elementMetadata: any) => {
            newMap[elementMetadata.hash] = elementMetadata;
          }
        );
      }
      return newMap;
    }, [projectElementMetadata]);

    let validElementsCodebaseIds = useMemo(() => {
      if (projectLaunchStatus !== "succeeded") return [];

      return Object.keys(treeElementLookup)?.filter((codebaseId: string) => {
        const element = treeElementLookup[codebaseId];
        const filenameWithoutExtension = element.filename.replace(
          /\.[^/.]+$/,
          ""
        );

        // Only allow stories
        if (element.type !== "storybook-component") {
          return false;
        }
        // Exclude libraries & templates
        if (element.library || element.isTemplateStory) {
          return false;
        }

        // Exclude Next JS app file
        if (
          (framework === "NEXT_JS" || framework === "REMIX") &&
          filenameWithoutExtension.endsWith("pages/_app")
        ) {
          return false;
        }

        // No unsaved components
        if (element.filename.includes(STORYBOARDS_FOLDER)) {
          return false;
        }

        return true;
      });
    }, [framework, projectLaunchStatus, treeElementLookup]);

    const validUngeneratedElements = useMemo(() => {
      if (!validElementsCodebaseIds?.length) return [];

      if (!projectElementMetadataLoaded) return [];

      return validElementsCodebaseIds?.filter((codebaseId: any) => {
        const element = treeElementLookup[codebaseId];
        const fileHash = element?.fileHash;

        if (!fileHash) {
          return false;
        }

        // Already generated
        if (hashToElementMetadata[fileHash]) {
          return false;
        }

        return true;
      });
    }, [
      validElementsCodebaseIds,
      treeElementLookup,
      projectElementMetadataLoaded,
    ]);

    const [processedCount, setProcessedCount] = useState(0);

    useEffect(() => {
      if (!projectElementMetadata) {
        return;
      }

      setMetadata((prev) => ({ ...prev, ...projectElementMetadata }));
    }, [projectElementMetadata]);

    useEffect(() => {
      const fetchData = async (codebaseId) => {
        dispatch(setIsFetching(true));
        const element = treeElementLookup[codebaseId];
        const elementProcessingInfo = processingInformation[codebaseId];

        const supabaseJWT = await getUnexpiredToken(session);

        try {
          const res = await axios.post(
            `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/canvases/${canvasId}/generateMetadata`,
            {
              treeElement: element,
              force: elementProcessingInfo && elementProcessingInfo.force,
            },
            {
              headers: {
                Authorization: `Bearer ${supabaseJWT}`,
              },
            }
          );
          return { data: res.data, error: null };
        } catch (error) {
          return { data: null, error: error.message };
        } finally {
          dispatch(setIsFetching(false));
          dispatch(dequeueElement());

          // Increment processed count and check if we need to refetch
          setProcessedCount((prev) => {
            const newCount = prev + 1;
            if (newCount >= 10) {
              triggerRefetchElementMetadata();
              return 0; // Reset counter
            }
            return newCount;
          });
        }
      };

      // Process the next item in the queue if not currently loading
      if (elementsToFetch.length > 0 && !isFetching) {
        fetchData(elementsToFetch[0]);
      } else if (elementsToFetch.length === 0 && processedCount > 0) {
        // Queue is empty and we have processed some elements, trigger final refetch
        triggerRefetchElementMetadata();
        setProcessedCount(0);
      }
    }, [elementsToFetch, isFetching]); // Refetch when reduxVariable changes

    useEffect(() => {
      if (projectLaunchStatus !== "succeeded") return;
      if (!validUngeneratedElements || !canvasId || !session) return;
      if (validUngeneratedElements.length === 0) return;

      // A queue already exists
      if (elementsToFetch.length > 0) {
        const newElementsToQueue = validUngeneratedElements.filter(
          (element) => !elementsToFetch.includes(element)
        );
        dispatch(enqueueElements(newElementsToQueue));
      } else {
        dispatch(initializeQueue(validUngeneratedElements));
      }
    }, [projectLaunchStatus, validUngeneratedElements]); // Refetch when reduxVariable changes

    useEffect(() => {
      if (regenerateEntireQueue) {
        dispatch(initializeQueue(shuffleArray(validElementsCodebaseIds)));
        dispatch(markElementsWithForce(validElementsCodebaseIds));
        dispatch(setRegenerateEntireQueue(false));
      }
    }, [regenerateEntireQueue, dispatch]);

    return (
      <ElementMetadataContext.Provider value={{ metadata }}>
        {children}
      </ElementMetadataContext.Provider>
    );
  }
);

export const useElementMetadata = (): any => {
  const context = useContext(ElementMetadataContext);
  if (!context) {
    throw new Error(
      "useElementMetadata must be used within an ElementMetadataProvider"
    );
  }
  return context;
};
